import Form from "@/components/Form";
import { Dialog, DialogContent } from "@/components/ui/dialog";
import { useProjectSchedulesContext } from "@/modules/projects/components/schedules/ProjectSchedulesContext";

import { memo, useEffect } from "react";
import { useRowSelection } from "@/components/data-table/hooks/useRowSelection";
import { ProjectSchedulesService } from "@/modules/projects/service/projectSchedule";
import { ProjectSchedulesDialogHeader } from "@/modules/projects/components/schedules/ProjectSchedulesDialogHeader";
import { ProjectSchedulesDeleteDialogForm } from "@/modules/projects/components/schedules/ProjectSchedulesDeleteDialogForm";
import { useMutation } from "@tanstack/react-query";
import { useToast } from "@/components/ui/use-toast";
import { useTranslation } from "react-i18next";
import { useProjectSchedulesDeleteForm } from "../../hooks/useProjectSchedulesDeleteForm";
import { ProjectSchedulesDeleteDialogFooter } from "./ProjectSchedulesDeleteDialogFooter";
import { ProjectSchedulesDeleteFormData } from "@/types/projectSchedulesDeleteForm";
import { useFormatters } from "@/lib/hooks/useFormatters";

const ProjectSchedulesDeleteDialogComponent = () => {
  const { toast } = useToast();
  const { t } = useTranslation();
  const { formatDate } = useFormatters();
  const {
    projectId,
    refetch,
    selectedStartDate: dateStart,
    selectedEndDate: dateEnd,
    isDeleteDialogOpened,
    setIsDeleteDialogOpened,
  } = useProjectSchedulesContext();
  const { getSelection, clear } = useRowSelection();

  const projectSchedulesMutation = useMutation({
    mutationFn: ({
      date_start,
      date_end,
      delete_all,
    }: ProjectSchedulesDeleteFormData) => {
      return ProjectSchedulesService.deleteSchedules(
        projectId,
        Array.from(getSelection()),
        delete_all ? undefined : { from_date: date_start, to_date: date_end },
      );
    },
    onSuccess: () => {
      setIsDeleteDialogOpened(false);
      form.reset();
      refetch();
      clear();
      toast({
        title: t("projectSchedules.success.schedulesDeleted"),
      });
    },
    onError: (error) => {
      toast({
        title: t("projectSchedules.error.schedulesNotDeleted"),
        description: error.message,
        variant: "destructive",
      });
    },
  });

  const isPending = projectSchedulesMutation.isPending;

  const { form, defaultValues } = useProjectSchedulesDeleteForm({
    dateStart: formatDate(dateStart),
    dateEnd: formatDate(dateEnd),
    disabled: isPending,
  });

  useEffect(() => {
    form.setValue("date_start", formatDate(dateStart));
    form.setValue("date_end", formatDate(dateEnd));
  }, [form, dateStart, dateEnd, formatDate]);

  const onOpenChange = (open: boolean) => {
    form.reset(defaultValues);
    projectSchedulesMutation.reset();
    setIsDeleteDialogOpened(open);
  };

  const handleSubmit = (values: ProjectSchedulesDeleteFormData) => {
    projectSchedulesMutation.mutate(values);
  };

  return (
    <Dialog open={isDeleteDialogOpened} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-[40dvw] min-w-[700px] max-h-[90dvh] overflow-y-auto border-2 border-destructive text-red-800">
        <ProjectSchedulesDialogHeader
          variant="destructive"
          title={t("projectSchedules.dialog.delete")}
        />
        <Form form={form} handleSubmit={handleSubmit} className="gap-6">
          <ProjectSchedulesDeleteDialogForm />
          <ProjectSchedulesDeleteDialogFooter disabled={isPending} />
        </Form>
      </DialogContent>
    </Dialog>
  );
};
export const ProjectSchedulesDeleteDialog = memo(
  ProjectSchedulesDeleteDialogComponent,
);
